import { Card, Typography } from 'antd';
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

import type { HealthStatus } from '@/api/portfolio/api';
import { type HealthColor, HealthDot } from '@/components/common/HealthIndicator';
import { Pill, type PillTone } from '@/components/common/Pill';
import '@/styles/project_details_styles/trendscard.scss';

const { Title, Text } = Typography;

type TrendStatus = 'Green' | 'Yellow' | 'Red' | 'pending' | HealthStatus;

const normalizeStatus = (status: TrendStatus): HealthStatus => status.toLowerCase() as HealthStatus;

const getStatusTone = (status: string): PillTone => {
  switch (status.toLowerCase()) {
    case 'green':
      return 'success';
    case 'yellow':
      return 'warning';
    case 'red':
      return 'error';
    case 'pending':
      return 'info';
    default:
      return 'info';
  }
};

const getStatusLabel = (status: TrendStatus): string => {
  const normalizedStatus = normalizeStatus(status);
  return normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1);
};

export interface TrendDataPoint {
  week: number;
  date: string;
  value: number;
  status?: TrendStatus;
}

export interface TrendItem {
  label: string;
  status: TrendStatus;
  dataPoints?: TrendDataPoint[];
  isComingSoon?: boolean;
}

export interface TrendsCardProps {
  trends: TrendItem[];
}

const getStatusColor = (status?: string): string => {
  switch (status?.toLowerCase()) {
    case 'green':
      return 'var(--state-success)';
    case 'yellow':
      return 'var(--state-warning)';
    case 'red':
      return 'var(--state-error)';
    default:
      return 'var(--border-default)';
  }
};

export function TrendsCard({ trends }: Readonly<TrendsCardProps>): React.ReactElement {
  return (
    <Card className="trends-card">
      <div className="trends-card__header">
        <Title level={5} className="trends-card__title">
          Trends - Past 12 Weeks
        </Title>
        <Pill tone="neutral">Source: Auto · weekly update history</Pill>
      </div>

      <div className="trends-card__content">
        {trends.map((trend) => (
          <div key={trend.label} className="trends-card__trend-row">
            {/* Label */}
            <div className="trends-card__trend-label">
              <Text type="secondary">{trend.label}</Text>
            </div>

            {/* Trend Line */}
            {trend.isComingSoon ? (
              <div className="trends-card__trend-line-coming-soon">
                <Text type="secondary">Coming in V2</Text>
              </div>
            ) : (
              <div className="trends-card__trend-line">
                {trend.dataPoints && trend.dataPoints.length > 0 && (
                  <ResponsiveContainer width="100%" height={32}>
                    <LineChart
                      data={trend.dataPoints}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="0" stroke="transparent" />
                      <XAxis dataKey="week" hide tick={false} />
                      <YAxis hide domain={[0, 100]} type="number" />

                      {/* Equal zone dividers (33, 67) */}
                      <ReferenceLine
                        y={0}
                        stroke="var(--state-error)"
                        strokeDasharray="2,2"
                        strokeWidth={1}
                      />
                      <ReferenceLine
                        y={50}
                        stroke="var(--state-warning)"
                        strokeDasharray="2,2"
                        strokeWidth={1}
                      />
                      <ReferenceLine
                        y={100}
                        stroke="var(--state-success)"
                        strokeDasharray="2,2"
                        strokeWidth={1}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)',
                        }}
                        formatter={(value) => {
                          if (value === 0) {
                            return ['Red', 'Value'];
                          }
                          if (value === 50) {
                            return ['Yellow', 'Value'];
                          }
                          if (value === 100) {
                            return ['Green', 'Value'];
                          }
                          return [`${value}%`, 'Value'];
                        }}
                        labelFormatter={(label) => {
                          // Find the corresponding date from dataPoints
                          const point = trend.dataPoints?.find((p) => p.week === label);
                          return point ? `${point.date}` : `Week ${label}`;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="var(--blend-navy)"
                        strokeWidth={2.5}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          const dotColor = getStatusColor(payload.status || trend.status);
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={2.5}
                              fill="white"
                              stroke={dotColor}
                              strokeWidth={1.5}
                            />
                          );
                        }}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}

            {/* Status Badge */}
            {trend.isComingSoon ? (
              <Pill tone="neutral">Coming in V2</Pill>
            ) : (
              <Pill
                tone={getStatusTone(trend.status)}
                leading={
                  normalizeStatus(trend.status) !== 'pending' ? (
                    <HealthDot status={normalizeStatus(trend.status) as HealthColor} />
                  ) : undefined
                }
              >
                {getStatusLabel(trend.status)}
              </Pill>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
